// Presentational product checkbox row — all state via props so it renders
// identically in the live intake form (wrapped by ProductCheckboxRow, which
// supplies redux state/dispatch) and in the admin branding preview (static
// sample state). Shows a checkbox for the product and (when checked) inline
// sub-options; the sub-option control follows the product's `selection_mode`:
// 'single' → radios, 'multiple' → checkboxes.
import { useState } from 'react';
import {
  Box, Button, Checkbox, Radio, RadioGroup, FormControlLabel, FormGroup, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { materialSymbolSx } from '../../theme.js';
import { renderableHtml } from '../../utils/rtf-helpers.js';

const OptionLabel = ({ name, description }) => (
  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
    <Box component="span" sx={{ fontWeight: 600 }}>{name}</Box>
    {description && (
      <Box component="span" sx={{ color: 'text.secondary' }}>{` — ${description}`}</Box>
    )}
  </Typography>
);

const ProductCheckboxRowView = ({
  product, options = [], isChecked, selectedOptionIds = [], onProductChange, onOptionToggle,
  includedItems = [], includedBy = null,
}) => {
  const isMultiple = product.selection_mode === 'multiple';
  // This card is auto-included by another selected product: it reads as a
  // settled "already taken care of" state (success chip, no primary ring) and
  // its own checkbox/options are non-interactive while the parent is selected.
  const isIncluded = Boolean(includedBy && includedBy.length);
  const includedByText = isIncluded ? includedBy.join(', ') : '';
  const showSelectedRing = isChecked && !isIncluded;
  const descId = `pcr-desc-${product.id}`;
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const images = product.images ?? [];
  const hasDetails = Boolean(product.long_description && product.long_description.trim());
  const detailsHtml = hasDetails ? renderableHtml(product.long_description) : '';
  const hasLearnMore = hasDetails || images.length > 0;
  const learnMoreTitleId = `pcr-learn-title-${product.id}`;

  const openLearnMore = () => {
    setActiveImage(0);
    setLearnMoreOpen(true);
  };
  const mainImage = images[activeImage] ?? images[0];

  const renderOptions = () => {
    if (isMultiple) {
      return (
        <FormGroup aria-label={`${product.name} options`}>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.id}
              sx={{ alignItems: 'flex-start', m: 0, mb: 0.5 }}
              control={(
                <Checkbox
                  size="small"
                  sx={{ p: 0, mr: 1, mt: 0.125 }}
                  checked={selectedOptionIds.includes(opt.id)}
                  onChange={() => onOptionToggle(String(opt.id))}
                />
              )}
              label={<OptionLabel name={opt.name} description={opt.short_description} />}
            />
          ))}
        </FormGroup>
      );
    }
    return (
      <RadioGroup
        aria-label={`${product.name} quality level`}
        name={`product-options-${product.id}`}
        value={selectedOptionIds[0] == null ? '' : String(selectedOptionIds[0])}
        onChange={(e) => onOptionToggle(e.target.value)}
      >
        {options.map((opt) => (
          <FormControlLabel
            key={opt.id}
            value={String(opt.id)}
            sx={{ alignItems: 'flex-start', m: 0, mb: 0.5 }}
            control={<Radio size="small" sx={{ p: 0, mr: 1, mt: 0.125 }} />}
            label={<OptionLabel name={opt.name} description={opt.short_description} />}
          />
        ))}
      </RadioGroup>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: showSelectedRing ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        opacity: isIncluded && !isChecked ? 0.92 : 1,
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: (t) => {
          const dark = t.palette.mode === 'dark';
          if (showSelectedRing) {
            return `0 0 0 1px ${t.palette.primary.main}, 0 6px 18px -10px rgba(0,0,0,${dark ? 0.7 : 0.25})`;
          }
          return dark
            ? '0 1px 2px rgba(0,0,0,0.4), 0 6px 18px -14px rgba(0,0,0,0.7)'
            : '0 1px 2px rgba(15,23,42,0.06), 0 6px 18px -14px rgba(15,23,42,0.25)';
        },
        '&:hover': { borderColor: showSelectedRing ? 'primary.main' : 'text.secondary' },
      }}
    >
      <FormControlLabel
        onChange={onProductChange}
        checked={isChecked || isIncluded}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          m: 0,
          px: 1.75,
          py: 1.5,
          width: '100%',
          cursor: isIncluded ? 'default' : 'pointer',
          '&:hover': { bgcolor: isIncluded ? 'transparent' : 'action.hover' },
          '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
        }}
        control={(
          <Checkbox
            size="small"
            sx={{ p: 0, mt: 0.25 }}
            color={isIncluded ? 'success' : 'primary'}
            slotProps={{
              input: {
                'aria-describedby': descId,
                ...(isIncluded
                  ? {
                    'aria-disabled': true,
                    'aria-label': `${product.name} — included with ${includedByText}, no additional charge`,
                  }
                  : {}),
              },
            }}
          />
        )}
        label={(
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {product.icon_key && (
              <Box component="span" aria-hidden="true" sx={{ ...materialSymbolSx, fontSize: 20, color: 'text.secondary' }}>
                {product.icon_key}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600, lineHeight: 1.3, flex: 1, minWidth: 0,
                  }}
                >
                  {product.name}
                </Typography>
                {isIncluded && (
                  <Tooltip title={`Included with ${includedByText}`}>
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      icon={<CheckCircleOutlineIcon />}
                      label="Included"
                      sx={{
                        height: 20,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', fontWeight: 600 },
                        '& .MuiChip-icon': { fontSize: 14, ml: 0.5 },
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography id={descId} variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
                {product.short_description}
              </Typography>
              {isIncluded && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block', mt: 0.25, color: 'success.main', fontWeight: 600,
                  }}
                >
                  {`Included with ${includedByText}`}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      />

      {hasLearnMore && (
        <Box sx={{
          pl: 5.25, pr: 1.75, pb: 1.25, mt: -0.5,
        }}
        >
          <Button
            variant="text"
            size="small"
            startIcon={<InfoOutlinedIcon />}
            onClick={openLearnMore}
            aria-haspopup="dialog"
            sx={{
              textTransform: 'none',
              fontSize: '0.78rem',
              px: 0.75,
              minHeight: 0,
              py: 0.25,
            }}
          >
            Learn more
          </Button>
        </Box>
      )}

      {isChecked && !isIncluded && options.length > 0 && (
        <Box
          sx={{
            pt: 1, pb: 1.5, pl: 5.25, pr: 1.75, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider',
          }}
        >
          {renderOptions()}
        </Box>
      )}

      {includedItems.length > 0 && (
        <Box
          role="group"
          aria-label={`Included with ${product.name} at no charge`}
          sx={{
            pl: 5.25,
            pr: 1.75,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: (t) => alpha(
              t.palette.text.primary,
              t.palette.mode === 'dark' ? 0.06 : 0.03,
            ),
          }}
        >
          <Typography
            component="div"
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.62rem',
            }}
          >
            <AccountTreeOutlinedIcon sx={{ fontSize: 14 }} />
            Included at no charge
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyle: 'none', m: 0, mt: 0.5, p: 0,
            }}
          >
            {includedItems.map((inc) => (
              <Box
                component="li"
                key={inc.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  lineHeight: 1.5,
                }}
              >
                <CheckCircleOutlineIcon sx={{
                  fontSize: 14, mt: '2px', color: 'success.main', flexShrink: 0,
                }}
                />
                <Box component="span">
                  {inc.name}
                  {inc.optionNames.length > 0 && (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {` — ${inc.optionNames.join(', ')}`}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {hasLearnMore && (
        <Dialog
          open={learnMoreOpen}
          onClose={() => setLearnMoreOpen(false)}
          maxWidth="sm"
          fullWidth
          aria-labelledby={learnMoreTitleId}
        >
          <DialogTitle
            id={learnMoreTitleId}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, pr: 6,
            }}
          >
            {product.icon_key && (
              <Box component="span" aria-hidden="true" sx={{ ...materialSymbolSx, fontSize: 24, color: 'text.secondary' }}>
                {product.icon_key}
              </Box>
            )}
            {product.name}
          </DialogTitle>
          <IconButton
            aria-label="Close"
            onClick={() => setLearnMoreOpen(false)}
            sx={{
              position: 'absolute', top: 8, right: 8, color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            {mainImage && (
              <Box sx={{ mb: hasDetails ? 2 : 0 }}>
                <Box
                  component="img"
                  src={`/api/catalog/images/${mainImage.id}`}
                  alt={mainImage.alt_text || `${product.name} image`}
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 360,
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                />
                {images.length > 1 && (
                  <Box sx={{
                    display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap',
                  }}
                  >
                    {images.map((img, index) => {
                      const selected = index === activeImage;
                      return (
                        <Box
                          key={img.id}
                          component="button"
                          type="button"
                          aria-label={`Show image ${index + 1}`}
                          aria-pressed={selected}
                          onClick={() => setActiveImage(index)}
                          sx={{
                            p: 0,
                            border: 2,
                            borderColor: selected ? 'primary.main' : 'transparent',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            lineHeight: 0,
                            bgcolor: 'transparent',
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                          }}
                        >
                          <Box
                            component="img"
                            src={`/api/catalog/images/${img.id}`}
                            alt={img.alt_text || `${product.name} thumbnail ${index + 1}`}
                            sx={{
                              display: 'block',
                              width: 64,
                              height: 48,
                              objectFit: 'cover',
                              opacity: selected ? 1 : 0.7,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
            {hasDetails && (
              <Box
                sx={{
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                  color: 'text.primary',
                  '& p': { mt: 0, mb: 1 },
                  '& h2': {
                    mt: 1.5, mb: 0.5, fontSize: '1.05rem', fontWeight: 700,
                  },
                  '& h3': {
                    mt: 1.5, mb: 0.5, fontSize: '0.95rem', fontWeight: 600,
                  },
                  '& ul, & ol': { pl: 3, mb: 1 },
                  '& li': { mb: 0.25 },
                  '& a': { color: 'primary.main' },
                  '& blockquote': {
                    borderLeft: '3px solid',
                    borderColor: 'divider',
                    pl: 2,
                    ml: 0,
                    color: 'text.secondary',
                  },
                }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: detailsHtml }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLearnMoreOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ProductCheckboxRowView;
